import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/projects.scss";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";
import { PROJECT_CATEGORIES } from "./projectCategoryData";


function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}



interface Project {
    title: string;
    deadline: string;
    description: string;
    link?: string;
}

interface PlannedProject {
    titleKey: TranslationKey;
    descKey: TranslationKey;
}

const PLANNED_PROJECT_KEYS: PlannedProject[] = [
    { titleKey: "planned_proj_justice_title", descKey: "planned_proj_justice_desc" },
    { titleKey: "planned_proj_documents_title", descKey: "planned_proj_documents_desc" },
    { titleKey: "planned_proj_deepfake_title", descKey: "planned_proj_deepfake_desc" },
    { titleKey: "planned_proj_wcag_title", descKey: "planned_proj_wcag_desc" },
    { titleKey: "planned_proj_environment_title", descKey: "planned_proj_environment_desc" },
    { titleKey: "planned_proj_missing_title", descKey: "planned_proj_missing_desc" },
    { titleKey: "planned_proj_border_title", descKey: "planned_proj_border_desc" },
    { titleKey: "planned_proj_comms_title", descKey: "planned_proj_comms_desc" },
    { titleKey: "planned_proj_phishing_title", descKey: "planned_proj_phishing_desc" },
    { titleKey: "planned_proj_illegal_goods_title", descKey: "planned_proj_illegal_goods_desc" },
    { titleKey: "planned_proj_media_title", descKey: "planned_proj_media_desc" },
    { titleKey: "planned_proj_academic_title", descKey: "planned_proj_academic_desc" },
    { titleKey: "planned_proj_mapping_title", descKey: "planned_proj_mapping_desc" },
    { titleKey: "planned_proj_skg_title", descKey: "planned_proj_skg_desc" },
    { titleKey: "planned_proj_ria_title", descKey: "planned_proj_ria_desc" },
];


const ProjectsPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const { ref: refIntro, inView: inViewIntro } = useInView(0.15);
    const { ref: refList, inView: inViewList } = useInView(0.1);
    const { ref: refPlanned, inView: inViewPlanned } = useInView(0.08);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const PROJECTS: Project[] = [
        { title: t("projects_cdn24_title"), deadline: t("projects_cdn24_deadline"), description: t("projects_cdn24_desc"), link: "https://cdn24.info" },
        { title: "Literis", deadline: t("projects_placeholder_deadline_1"), description: t("projects_literis_desc") },
    ];

    const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
    const bodyRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const toggleCard = (idx: number) => {
        const el = bodyRefs.current[idx];
        if (!el) return;

        const isCurrentlyExpanded = !!expandedCards[idx];

        if (!isCurrentlyExpanded) {
            // EXPANDING: lock current height → animate to scrollHeight → then "none"
            const from = el.offsetHeight;
            el.style.maxHeight = `${from}px`;
            // force reflow so the browser registers the starting value
            void el.offsetHeight;
            el.style.maxHeight = `${el.scrollHeight}px`;

            const onEnd = () => {
                el.style.maxHeight = "none";
                el.removeEventListener("transitionend", onEnd);
            };
            el.addEventListener("transitionend", onEnd);
        } else {
            // COLLAPSING: lock current height → animate down to collapsed
            el.style.maxHeight = `${el.scrollHeight}px`;
            // force reflow
            void el.offsetHeight;
            el.style.maxHeight = "5.5em";
        }

        setExpandedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
    };



    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("projects-page", { "projects-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("projects_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

            {/* ===== HERO ===== */}
            <MatrixHero
                title={t("projects_hero_title")}
                subtitle={t("projects_hero_subtitle")}
                density={0.5}
            />

            <div className="projects-page__inner">

                {/* ===== WPROWADZENIE ===== */}
                <article
                    ref={refIntro}
                    className={classNames("proj-card", { "proj-card--visible": inViewIntro })}
                >
                    <div className="proj-card__glow" aria-hidden="true" />
                    <h2 className="proj-card__title">{t("projects_intro_title")}</h2>
                    <p className="proj-card__text">
                        <TypewriterText text={t("projects_intro_text1")} active={inViewIntro} speed={1} />
                        <TypewriterText text={t("projects_intro_text2")} active={inViewIntro} speed={1} />
                    </p>
                </article>

                {/* ===== LISTA PROJEKTÓW ===== */}
                <div
                    ref={refList}
                    className={classNames("proj-list-section", { "proj-list-section--visible": inViewList })}
                >
                    <h2 className="proj-list-section__title">{t("projects_list_title")}</h2>

                    <ul className="proj-list">
                        {PROJECTS.map((project, idx) => (
                            <li
                                key={idx}
                                className="proj-item"
                                style={{ animationDelay: `${idx * 0.1}s` }}
                            >
                                {project.link ? (
                                    <a
                                        href={project.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="proj-item__link"
                                        draggable={false}
                                    >
                                        <div className="proj-item__header">
                                            <h3 className="proj-item__title">{project.title}</h3>
                                            <span className="proj-item__deadline">
                                                {/* {project.deadline} */}
                                            </span>
                                        </div>
                                        <p className="proj-item__desc">{project.description}</p>
                                        <span className="proj-item__cta">
                                            {t("projects_visit")}
                                            <svg className="proj-item__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </span>
                                    </a>
                                ) : (
                                    <>
                                        <div className="proj-item__header">
                                            <h3 className="proj-item__title">{project.title}</h3>
                                            <span className="proj-item__deadline">
                                                {/* {project.deadline} */}
                                            </span>
                                        </div>
                                        {project.description && project.description.split("\n\n").map((para, pIdx) => (
                                            <p key={pIdx} className="proj-item__desc">{para}</p>
                                        ))}
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ===== PLANNED PROJECTS ===== */}
                <div
                    ref={refPlanned}
                    className={classNames("planned-section", { "planned-section--visible": inViewPlanned })}
                >

                    <div className="planned-cta">
                        <p className="planned-cta__text">{t("planned_invite")}</p>
                        <a
                            href="mailto:cc@wsiz.edu.pl"
                            className="planned-cta__email"
                            aria-label={`${t("planned_invite")} — ${t("planned_invite_email_label")}`}
                        >
                            {t("planned_invite_email_label")}
                        </a>
                    </div>

                    <div className="project-category-hub">
                        <div className="project-category-hub__intro">
                            <h3 className="project-category-hub__title">
                                {t("projects_topic_cards_title")}
                            </h3>
                            <p className="project-category-hub__text">
                                {t("projects_topic_cards_intro")}
                            </p>
                        </div>

                        <div className="project-category-hub__grid">
                            {PROJECT_CATEGORIES.map((category, index) => (
                                <Link
                                    key={category.id}
                                    to={category.path}
                                    className="project-category-tile"
                                    style={{ animationDelay: `${0.28 + index * 0.08}s` }}
                                >
                                    <div className="project-category-tile__glow" aria-hidden="true" />
                                    <div className="project-category-tile__media">
                                        <img
                                            src={category.backgroundIcon}
                                            alt={t(category.titleKey)}
                                            className="project-category-tile__icon"
                                            draggable={false}
                                        />
                                    </div>
                                    <div className="project-category-tile__content">
                                        <h4 className="project-category-tile__title">
                                            {t(category.titleKey)}
                                        </h4>
                                        <p className="project-category-tile__summary">
                                            {t(category.summaryKey)}
                                        </p>
                                        <ul className="project-category-tile__previewList">
                                            {category.cardPreviewTitleKeys.map((key) => (
                                                <li key={key} className="project-category-tile__previewItem">
                                                    {t(key)}
                                                </li>
                                            ))}
                                        </ul>
                                        <span className="project-category-tile__cta">
                                            {t("projects_topic_open")}
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* <div className="planned-grid">
                        {(() => {
                            const cols: number[][] = [[], [], []];
                            PLANNED_PROJECT_KEYS.forEach((_, idx) => {
                                cols[idx % 3].push(idx);
                            });
                            return cols.map((colIdxs, colIdx) => (
                                <div key={colIdx} className="planned-grid__col">
                                    {colIdxs.map(idx => {
                                        const proj = PLANNED_PROJECT_KEYS[idx];
                                        const isExpanded = !!expandedCards[idx];
                                        const paragraphs = t(proj.descKey).split("\n\n");
                                        return (
                                            <article
                                                key={idx}
                                                className={classNames("planned-card", { "planned-card--expanded": isExpanded })}
                                                style={{ animationDelay: `${idx * 0.06}s` }}
                                            >
                                                <h3 className="planned-card__title">{t(proj.titleKey)}</h3>
                                                <div
                                                    ref={el => { bodyRefs.current[idx] = el; }}
                                                    className={classNames("planned-card__body", { "planned-card__body--collapsed": !isExpanded })}
                                                >
                                                    {paragraphs.map((para, pIdx) => (
                                                        <p key={pIdx} className="planned-card__text">{para}</p>
                                                    ))}
                                                </div>
                                                {paragraphs.length > 1 && (
                                                    <button
                                                        className="planned-card__toggle"
                                                        onClick={() => toggleCard(idx)}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {isExpanded ? "—" : "+"}
                                                    </button>
                                                )}
                                            </article>
                                        );
                                    })}
                                </div>
                            ));
                        })()}
                    </div> */}
                </div>

            </div>
        </section>
    );
};

export default ProjectsPage;
